<template>
  <BaseModal
    :is-visible="isVisible"
    title="Staff Profile"
    :subtitle="employee ? `${employee.first_name} ${employee.last_name}'s full profile & records` : 'Staff profile details'"
    :icon="UserCircle"
    size="4xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >
    <div v-if="employee" class="space-y-6">
      <!-- Profile Header Summary Banner -->
      <div class="p-6 rounded-2xl bg-gradient-to-r from-gray-50 via-white to-gray-50 dark:from-gray-800/80 dark:via-gray-800 dark:to-gray-800/80 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col sm:flex-row items-center sm:items-start gap-6">
        <!-- Avatar -->
        <div class="flex-shrink-0">
          <div class="h-24 w-24 rounded-full bg-secondary/10 dark:bg-secondary-dark/20 text-secondary dark:text-secondary-dark-text flex items-center justify-center overflow-hidden ring-4 ring-white dark:ring-gray-700 shadow-md border-2 border-gray-200 dark:border-gray-600">
            <img
              v-if="employee.user_image && !imageErrored"
              :src="employee.user_image"
              :alt="employee.full_name"
              class="h-full w-full object-cover rounded-full"
              @error="imageErrored = true"
            />
            <span v-else class="text-3xl font-bold tracking-tight">
              {{ initials }}
            </span>
          </div>
        </div>

        <!-- Details & Quick Metrics -->
        <div class="flex-1 text-center sm:text-left min-w-0">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h2 class="text-2xl font-bold text-gray-900 dark:text-white truncate">
                {{ employee.first_name }} {{ employee.middle_name ? employee.middle_name + ' ' : '' }}{{ employee.last_name }}
              </h2>
              <p class="text-sm font-medium text-secondary dark:text-secondary-dark-text mt-0.5">
                {{ employee.position || 'Staff Member' }}
                <span v-if="employee.department" class="text-gray-400 dark:text-gray-500">
                  • {{ employee.department }}
                </span>
              </p>
            </div>

            <!-- Status Pill -->
            <div class="inline-flex items-center gap-2 self-center sm:self-start">
              <span
                :class="[
                  'px-3 py-1 text-xs font-semibold rounded-full border',
                  employee.status === 'Active'
                    ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-800'
                    : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800'
                ]"
              >
                {{ employee.status }}
              </span>
            </div>
          </div>

          <!-- Quick badges -->
          <div class="mt-4 flex flex-wrap items-center justify-center sm:justify-start gap-3 text-xs text-gray-600 dark:text-gray-300">
            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700/60 font-medium">
              <Mail class="w-3.5 h-3.5 text-gray-400" />
              {{ employee.email }}
            </span>
            <span v-if="employee.phone_number" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700/60 font-medium">
              <Phone class="w-3.5 h-3.5 text-gray-400" />
              {{ employee.phone_number }}
            </span>
            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 font-semibold border border-purple-200 dark:border-purple-800">
              <FileCheck class="w-3.5 h-3.5 text-purple-500" />
              {{ employee.total_documents || 0 }} Documents on File
            </span>

            <!-- Work State Badge -->
            <span
              v-if="employee.work_state"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold border"
              :class="[
                employee.work_state === 'Maryland'
                  ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800'
                  : employee.work_state === 'DC'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                  : 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800'
              ]"
            >
              <MapPin class="w-3.5 h-3.5" />
              <span>State: {{ employee.work_state === 'Both' ? 'MD & DC' : employee.work_state }}</span>
            </span>

            <!-- Service Population Badge -->
            <span
              v-if="employee.service_population"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold border"
              :class="[
                employee.service_population === 'Adults'
                  ? 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800'
                  : employee.service_population === 'Kids'
                  ? 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/40 dark:text-pink-300 dark:border-pink-800'
                  : 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800'
              ]"
            >
              <Users class="w-3.5 h-3.5" />
              <span>Clients: {{ employee.service_population === 'Both' ? 'Adults & Kids' : employee.service_population }}</span>
            </span>

            <!-- Application Origin Indicator Badge -->
            <button
              v-if="employee.has_application && employee.application_id"
              type="button"
              @click="$emit('view-application', employee)"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 font-semibold border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition cursor-pointer"
              :title="`Approved via Application System (App #${employee.application_id}) - Click to view full application details`"
            >
              <FileCheck class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              <span>System Approved Applicant (App #{{ employee.application_id }})</span>
            </button>
            <span
              v-else
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300 font-medium border border-gray-200 dark:border-gray-700"
              title="Staff account created directly via Register Staff"
            >
              <UserPlus class="w-3.5 h-3.5 text-gray-500" />
              <span>Direct Registration</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Segmented Tabs Navigation (Flex wrapped, eliminating horizontal scrollbar) -->
      <div class="border-b border-gray-200 dark:border-gray-700 pb-2">
        <nav class="flex flex-wrap items-center gap-1.5 sm:gap-2" aria-label="Tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            @click="activeTab = tab.id"
            :class="[
              'px-3 sm:px-3.5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-150 flex items-center gap-2',
              activeTab === tab.id
                ? 'bg-secondary text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
            ]"
          >
            <component :is="tab.icon" class="w-4 h-4 flex-shrink-0" />
            <span>{{ tab.label }}</span>
            <span
              v-if="tab.badge !== undefined"
              :class="[
                'px-1.5 py-0.5 text-[10px] rounded-full font-bold',
                activeTab === tab.id
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              ]"
            >
              {{ tab.badge }}
            </span>
          </button>
        </nav>
      </div>

      <!-- Tab Content: Personal Information -->
      <div v-show="activeTab === 'personal'" class="space-y-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">First Name</span>
            <p :class="['text-base font-semibold mt-1', valueClass(employee.first_name)]">{{ employee.first_name || 'Not Provided' }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Middle Name</span>
            <p :class="['text-base font-semibold mt-1', valueClass(employee.middle_name)]">{{ employee.middle_name || 'Not Specified' }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Last Name</span>
            <p :class="['text-base font-semibold mt-1', valueClass(employee.last_name)]">{{ employee.last_name || 'Not Provided' }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Date of Birth</span>
            <p :class="['text-base font-semibold mt-1', valueClass(employee.date_of_birth)]">{{ formatDate(employee.date_of_birth) }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Gender</span>
            <p :class="['text-base font-semibold mt-1', valueClass(employee.gender)]">{{ employee.gender || 'Not Specified' }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">SSN (Last 4)</span>
            <p :class="['text-base font-semibold mt-1', valueClass(employee.ssn_last_four)]">
              {{ employee.ssn_last_four ? `***-**-${employee.ssn_last_four}` : 'Not Provided' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Tab Content: Contact & Address -->
      <div v-show="activeTab === 'contact'" class="space-y-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Email Address</span>
            <p :class="['text-base font-semibold mt-1 break-all', valueClass(employee.email)]">{{ employee.email || 'Not Provided' }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Phone Number</span>
            <p :class="['text-base font-semibold mt-1', valueClass(employee.phone_number)]">{{ employee.phone_number || 'Not Provided' }}</p>
          </div>
        </div>

        <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60 space-y-3">
          <div class="flex items-center gap-2">
            <MapPin class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
            <h4 class="text-sm font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">Residential Address</h4>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-1">
            <div class="sm:col-span-2">
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">STREET ADDRESS</span>
              <p :class="['text-sm font-semibold mt-0.5', valueClass(employee.street_address)]">{{ employee.street_address || 'Not Provided' }}</p>
            </div>
            <div>
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">CITY</span>
              <p :class="['text-sm font-semibold mt-0.5', valueClass(employee.city)]">{{ employee.city || 'Not Provided' }}</p>
            </div>
            <div>
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">STATE / ZIP CODE</span>
              <p class="text-sm font-semibold mt-0.5" :class="valueClass(employee.state || employee.zip_code)">
                {{ employee.state || '' }} {{ employee.zip_code || '' }}
                <span v-if="!employee.state && !employee.zip_code">Not Provided</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Content: Employment Details -->
      <div v-show="activeTab === 'employment'" class="space-y-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Position / Job Title</span>
            <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ employee.position || 'Not Assigned' }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Department</span>
            <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ employee.department || 'Not Assigned' }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Date of Hire</span>
            <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ formatDate(employee.hire_date) }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">User Role</span>
            <p class="text-base font-semibold text-gray-900 dark:text-white mt-1 capitalize">{{ employee.user_type_label || 'Employee' }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Account Created</span>
            <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ formatDate(employee.created_at) }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Account Status</span>
            <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ employee.status }}</p>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Work State / Jurisdiction</span>
            <div class="mt-1 flex items-center gap-2">
              <span
                v-if="employee.work_state"
                class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold"
                :class="[
                  employee.work_state === 'Maryland'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300'
                    : employee.work_state === 'DC'
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300'
                    : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-300'
                ]"
              >
                {{ employee.work_state === 'Both' ? 'Both (Maryland & DC)' : employee.work_state }}
              </span>
              <span v-else class="text-base font-semibold text-gray-400 dark:text-gray-500 italic">Not Specified</span>
            </div>
          </div>
          <div class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Client Group / Service Population</span>
            <div class="mt-1 flex items-center gap-2">
              <span
                v-if="employee.service_population"
                class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold"
                :class="[
                  employee.service_population === 'Adults'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300'
                    : employee.service_population === 'Kids'
                    ? 'bg-pink-100 text-pink-800 dark:bg-pink-900/60 dark:text-pink-300'
                    : 'bg-teal-100 text-teal-800 dark:bg-teal-900/60 dark:text-teal-300'
                ]"
              >
                {{ employee.service_population === 'Both' ? 'Both (Adults & Kids)' : employee.service_population }}
              </span>
              <span v-else class="text-base font-semibold text-gray-400 dark:text-gray-500 italic">Not Specified</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Content: Emergency Contact -->
      <div v-show="activeTab === 'emergency'" class="space-y-4">
        <div class="p-5 rounded-2xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40">
          <div class="flex items-center gap-2 mb-4">
            <AlertTriangle class="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h4 class="text-base font-bold text-gray-900 dark:text-white">Emergency Contact Information</h4>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div class="p-4 rounded-xl bg-white dark:bg-gray-800 border border-amber-100 dark:border-gray-700">
              <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Contact Person</span>
              <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ employee.emergency_contact_name || 'Not Provided' }}</p>
            </div>
            <div class="p-4 rounded-xl bg-white dark:bg-gray-800 border border-amber-100 dark:border-gray-700">
              <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Relationship</span>
              <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ employee.emergency_contact_relation || 'Not Provided' }}</p>
            </div>
            <div class="p-4 rounded-xl bg-white dark:bg-gray-800 border border-amber-100 dark:border-gray-700">
              <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Phone Number</span>
              <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ employee.emergency_contact_phone || 'Not Provided' }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Content: Documents & Files Overview -->
      <div v-show="activeTab === 'documents'" class="space-y-4">
        <div class="p-6 rounded-2xl bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 text-center space-y-4">
          <div class="w-14 h-14 mx-auto rounded-2xl bg-secondary/10 dark:bg-secondary-dark/20 text-secondary dark:text-secondary-dark-text flex items-center justify-center">
            <Files class="w-7 h-7" />
          </div>
          <div>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">Employee Documents & Credentials</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
              Manage certifications, licenses, background checks, manual/paper application records, and credentials for this staff member.
            </p>
          </div>

          <div class="flex flex-wrap justify-center gap-3 pt-2">
            <BaseButton
              text="Manage & View Documents"
              variant="primary"
              :icon="FolderOpen"
              @click="$emit('manageDocuments', employee)"
            />
            <BaseButton
              text="Upload New Document"
              variant="outline"
              :icon="UploadCloud"
              @click="$emit('uploadDocuments', employee)"
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center gap-2">
          <BaseButton
            text="Documents"
            variant="outline"
            size="sm"
            :icon="FileText"
            @click="$emit('manageDocuments', employee)"
          />
          <BaseButton
            text="Upload Files"
            variant="ghost"
            size="sm"
            :icon="UploadCloud"
            @click="$emit('uploadDocuments', employee)"
          />
          <BaseButton
            v-if="employee.has_application && employee.application_id"
            text="View Application"
            variant="outline"
            size="sm"
            :icon="FileCheck"
            class="text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-700 dark:hover:bg-emerald-950/40"
            @click="$emit('view-application', employee)"
          />
        </div>

        <div class="flex items-center gap-2">
          <BaseButton
            text="Edit Profile"
            variant="secondary"
            size="sm"
            :icon="Edit3"
            @click="$emit('edit', employee)"
          />
          <BaseButton
            text="Close"
            variant="primary"
            size="sm"
            @click="closeModal"
          />
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import type { employee } from '@/types/employee'
import {
  UserCircle,
  Mail,
  Phone,
  MapPin,
  FileCheck,
  User,
  UserPlus,
  Briefcase,
  AlertTriangle,
  Files,
  FolderOpen,
  UploadCloud,
  FileText,
  Edit3,
  Users,
} from 'lucide-vue-next'

const props = defineProps<{
  isVisible: boolean
  employee: employee | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'edit', employee: employee | null): void
  (e: 'manageDocuments', employee: employee | null): void
  (e: 'uploadDocuments', employee: employee | null): void
  (e: 'view-application', employee: employee | null): void
}>()

const activeTab = ref('personal')
const imageErrored = ref(false)

watch(() => props.employee, () => {
  imageErrored.value = false
})

const initials = computed(() => {
  if (!props.employee) return 'ST'
  const first = props.employee.first_name?.charAt(0) || ''
  const last = props.employee.last_name?.charAt(0) || ''
  return (first + last).toUpperCase() || 'ST'
})

const tabs = computed(() => [
  { id: 'personal', label: 'Personal Info', icon: User },
  { id: 'contact', label: 'Contact & Address', icon: MapPin },
  { id: 'employment', label: 'Employment', icon: Briefcase },
  { id: 'emergency', label: 'Emergency Contact', icon: AlertTriangle },
  { 
    id: 'documents', 
    label: 'Documents', 
    icon: Files,
    badge: props.employee?.total_documents ?? 0
  },
])

const valueClass = (value?: string | null) => {
  return value ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
}

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return 'Not Provided'
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

const closeModal = () => {
  emit('close')
}
</script>

